import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { ButtonLoader } from "Components";
import { newAuthService } from "Service/Axios";
import { saveUserProfileUrl } from "Constant/api";
import { Validator } from "Utils/validator";
import { GENDER_OPTIONS, VIEW_PROFILE_SECTIONS } from "Constant/constants";

export const PersonalDetailsModal = ({
  show,
  personalDetailsData,
  toggle,
  fetchData,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    dateOfBirth: "",
    gender: "",
    nationality: "",
    address: "",
    currentCity: "",
    currentCountry: "",
  });

  const [errMsg, setErrMsg] = useState({
    gender: "",
  });

  const {
    address,
    gender,
    dateOfBirth,
    nationality,
    currentCity,
    currentCountry,
  } = formData;

  const validationRequiredFields = {
    gender: {
      blankCheck: Validator.text,
    },
  };

  useEffect(() => {
    if (personalDetailsData) {
      setFormData({
        dateOfBirth: personalDetailsData.dateOfBirth || "",
        gender: personalDetailsData.gender || "",
        nationality: personalDetailsData.nationality || "",
        address: personalDetailsData.address || "",
        currentCity: personalDetailsData.currentCity || "",
        currentCountry: personalDetailsData.currentCountry || "",
      });
    }
  }, [personalDetailsData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const fieldRule = validationRequiredFields[name];

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (fieldRule) {
      if (fieldRule.blankCheck && !fieldRule.blankCheck(value)) {
        setErrMsg((prev) => ({
          ...prev,
          [name]: t(`common.messages.error.common.${name}.blank`),
        }));
      }
      // else if (fieldRule?.inValidCheck && !fieldRule.inValidCheck(value)) {
      //   setErrMsg((prev) => ({
      //     ...prev,
      //     [name]: t(`common.messages.error.common.${name}.inValid`),
      //   }));
      // }
      else {
        setErrMsg((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      let isValid = true;
      const errors = {};

      for (const field in validationRequiredFields) {
        const value = formData[field];
        const rule = validationRequiredFields[field];

        // Blank check
        if (rule.blankCheck && !rule.blankCheck(value)) {
          errors[field] = t(`common.messages.error.common.${field}.blank`);
          isValid = false;
        }
        // Invalid format check (only if blank check passed)
        // else if (rule.inValidCheck && !rule.inValidCheck(value)) {
        //   errors[field] = t(`common.messages.error.common.${field}.inValid`);
        //   isValid = false;
        // }
      }

      setErrMsg((prev) => ({
        ...prev,
        ...errors,
      }));

      resolve(isValid);
    });
  };

  const onSavePersonalDetails = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      try {
        const payload = {
          key: VIEW_PROFILE_SECTIONS.personalDetails,
          [VIEW_PROFILE_SECTIONS.personalDetails]: {
            ...personalDetailsData,
            dateOfBirth,
            gender,
            nationality,
            address,
            currentCity,
            currentCountry,
          },
        };
        setLoading(true);
        const response = await newAuthService(
          saveUserProfileUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(
            t("common.messages.notification.success.profileUpdated")
          );
          fetchData();
          toggle();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal show={show} onHide={toggle} className="highlightsModal">
      <Modal.Header closeButton>
        <Modal.Title className="text-md font-medium">
          {t("auth.viewProfile.editPersonalDetails")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-6 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="dateOfBirth" className="form-label">
                {t("common.label.dateOfBirth")}
              </label>
              <input
                type="date"
                className="form-control"
                id="dateOfBirth"
                name="dateOfBirth"
                value={dateOfBirth}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-md-6 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="gender" className="form-label">
                {t("common.label.gender")}{" "}
                <span className="text-danger">*</span>
              </label>
              <select
                className="form-control"
                id="gender"
                name="gender"
                value={gender}
                onChange={handleChange}
              >
                <option value="">{t("common.placeHolder.gender")}</option>
                {GENDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
              {errMsg.gender && <p className="error">{errMsg.gender}</p>}
            </div>
          </div>
          <div className="col-md-12 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="nationality" className="form-label">
                {t("common.label.nationality")}
              </label>
              <input
                type="text"
                className="form-control"
                id="nationality"
                name="nationality"
                placeholder={t("common.placeHolder.nationality")}
                value={nationality}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="col-md-12 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="address" className="form-label">
                {t("common.label.address")}
              </label>
              <input
                type="text"
                className="form-control"
                id="address"
                name="address"
                placeholder={t("common.placeHolder.address")}
                value={address}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-md-6 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="currentCity" className="form-label">
                {t("common.label.currentCity")}
              </label>
              <input
                type="text"
                className="form-control"
                id="currentCity"
                name="currentCity"
                placeholder={t("common.placeHolder.currentCity")}
                value={currentCity}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-md-6 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="currentCountry" className="form-label">
                {t("common.label.currentCountry")}
              </label>
              <input
                type="text"
                className="form-control"
                id="currentCountry"
                name="currentCountry"
                placeholder={t("common.placeHolder.currentCountry")}
                value={currentCountry}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={toggle}>
          {t("common.button.cancel")}
        </Button>
        {loading ? (
          <ButtonLoader />
        ) : (
          <Button variant="primary" onClick={onSavePersonalDetails}>
            {t("common.button.save")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
